import {Content} from 'common/types/app-api/content';
import {reporter} from 'common/report/error-reporting';
import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';

import {trackContentAction, TrackingEvent, TrackingSource} from '../tracking';

export async function performContentAction<T>({
  content,
  action,
  onSuccess,
  onError,
  onComplete,
  successMessage,
  errorMessage,
  initialTrackingEvent,
  initialTrackingProps,
  successTrackingEvent,
  successTrackingProps,
  errorTrackingEvent,
  errorTrackingProps,
  source,
}: {
  content: Partial<Content>;
  action: () => Promise<T>;
  onSuccess?: (result: T) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
  successMessage?: string;
  errorMessage?: string;
  initialTrackingEvent?: TrackingEvent;
  initialTrackingProps?: Obj;
  successTrackingEvent?: TrackingEvent;
  successTrackingProps?: Obj;
  errorTrackingEvent?: TrackingEvent;
  errorTrackingProps?: Obj;
  source?: TrackingSource;
}): Promise<T | undefined> {
  if (initialTrackingEvent) {
    trackContentAction({
      event: initialTrackingEvent,
      props: initialTrackingProps,
      content,
      source,
    });
  }
  try {
    const result = await action();
    if (successTrackingEvent) {
      trackContentAction({
        event: successTrackingEvent,
        props: {
          success: true,
          ...(successTrackingProps ?? {}),
        },
        content,
        source,
      });
    }
    onSuccess?.(result);
    if (successMessage) {
      addToast({
        type: ToastType.SUCCESS,
        message: successMessage,
        timeout: 20000,
      });
    }
    return result;
  } catch (err) {
    reporter.error(err as Error, errorMessage);
    if (errorTrackingEvent) {
      trackContentAction({
        event: errorTrackingEvent,
        props: {
          success: false,
          ...(errorTrackingProps ?? {}),
        },
        content,
        source,
      });
    }
    if (onError) {
      onError(err);
    }
    if (errorMessage) {
      addToast({
        type: ToastType.ERROR,
        message: errorMessage,
        timeout: 20000,
      });
    }
    return undefined;
  } finally {
    if (onComplete) {
      onComplete();
    }
  }
}
